import { useTranslation } from "react-i18next";
import { useHeaderTeamNameSelector } from "pages/team_settings/hooks";
import { useAllTeams } from "hooks/useSubTeams";
import { Team } from "models/schemas";
import { GaiaConstants } from "utils/gaia-constants";

/**
 * Page subtitle for the team-scoped list pages: audits, notices, litigations,
 * tasks, reviews.
 *
 * Header-scoped on purpose: resolves the header team id against `allTeams`,
 * not `selectedTeamMainMetaData`, which tracks the loaded RESOURCE's team and
 * so diverges from the header for a sub-team-owned object. Reading from redux
 * keeps it reactive — a mount-time localStorage snapshot could not track a
 * header team switch that left the page mounted.
 *
 * Do not remove the localStorage fallback: it keeps both the guest check and
 * the team label correct on the first render, before `headerSelectedTeam` and
 * `allTeams` land, and still covers `allTeams` landing with no description for
 * the header team.
 *
 * @param i18nKeyPrefix namespace holding `description`, `description_no_team`
 * and `guest_description` — for example "pages.audits".
 */
export const useListPageDescription = (i18nKeyPrefix: string): string => {
  const { t } = useTranslation();
  const teamName = useHeaderTeamNameSelector();
  const allTeams = useAllTeams();

  const selectedTeam =
    teamName || localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME);
  if (selectedTeam === GaiaConstants.GUEST_TEAM) {
    return String(t(`${i18nKeyPrefix}.guest_description`));
  }

  // Resolved straight off the `allTeams` entry rather than through
  // `useSubTeams`' `getTeamDescription`, whose fallback chain bottoms out at
  // the raw team id and so would shadow the localStorage fallback.
  const headerTeam = allTeams.find((team: Team) => team.teamId === teamName);

  const team =
    headerTeam?.teamDescription ||
    localStorage.getItem(GaiaConstants.SELECTED_TEAM_DESCRIPTION) ||
    "";

  // Without a team label the interpolated copy would render a double space and
  // an orphaned "team", so fall back to a dedicated no-team string.
  if (!team) {
    return String(t(`${i18nKeyPrefix}.description_no_team`));
  }

  return String(t(`${i18nKeyPrefix}.description`, { team }));
};
